import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";

/**
 * Skrzynka eop@elektim.pl — korespondencja z ENERGA.
 *
 * Dwie rzeczy, które muszą być widoczne w kodzie, a nie tylko w regulaminie:
 *
 * 1. NIC NIE WYCHODZI SAMO. Napisanie odpowiedzi tworzy KOPIĘ ROBOCZĄ
 *    (`useReplyDraft` / `useCreateDraft`); dopiero `useSendDraft`, wołane
 *    z kliknięcia człowieka, wysyła. Nie ma tu żadnego automatycznego wywołania
 *    wysyłki — również w `onSuccess`.
 * 2. NIE RUSZAMY SKRZYNKI. Nie ma metody „oznacz jako przeczytane" ani
 *    „przenieś" — stan skrzynki należy do ludzi pracujących w Outlooku.
 */

export type EopDirection = "incoming" | "outgoing";
export type EopTagSource =
  | "thread"
  | "sender"
  | "number"
  | "parcel"
  | "rule"
  | "ai"
  | "manual"
  | "";

/** Skuteczność jednej warstwy rozpoznawania. */
export interface EopTaggingLayer {
  source: EopTagSource;
  label: string;
  /** Ile przypięć tej warstwy aktualnie stoi (automat, nikt nie ruszył). */
  assigned: number;
  /** Ile wskazań warstwy człowiek jawnie potwierdził. */
  accepted: number;
  /** Ile przypięć warstwy człowiek poprawił na inne przedsięwzięcie. */
  corrected: number;
}
export type EopDraftStatus = "draft" | "sent" | "discarded" | "failed";

export interface EopProjectStub {
  id: string;
  name: string;
  short_name: string;
}

export interface EopAttachment {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number | null;
  is_inline: boolean;
  managed_file: string | null;
  /** null także wtedy, gdy plik czeka na wynik skanowania — nie tylko przy błędzie. */
  url: string | null;
  scan_status: ScanStatus | "";
  download_error: string;
  is_available: boolean;
}

export interface EopMessage {
  id: string;
  subject: string;
  body_preview: string;
  direction: EopDirection;
  from_address: string;
  from_name: string;
  to_addresses: string[];
  received_at: string;
  has_attachments: boolean;
  attachment_count: number;
  conversation_id: string;
  is_thread_latest: boolean;
  project: EopProjectStub | null;
  suggested_project: EopProjectStub | null;
  suggestion_source: EopTagSource;
  suggestion_reason: string;
  tag_source: EopTagSource;
  tagged_at: string | null;
  /** Identyfikator rozmowy — `conversation_id`, a bez niego id samej wiadomości. */
  thread_key: string;
  thread_count: number;
  /** Kto zrobił ostatni ruch: `them` = czekamy my, `us` = czekamy na operatora. */
  last_move: { by: "them" | "us"; who: string; at: string };
  /** Stan czytania w systemie (lustrzany do Exchange przy jawnej zmianie). */
  read_state: "unread" | "read" | "action";
  /** Agregaty wątku: czy jest coś nieprzeczytanego / z flagą „wymaga akcji". */
  thread_unread: boolean;
  thread_action: boolean;
  /** Decyzja człowieka: korespondencja nie dotyczy jednego przedsięwzięcia. */
  is_general: boolean;
  /** Propozycja AI, że to korespondencja ogólna — czeka na potwierdzenie. */
  suggested_general: boolean;
}

export interface EopMessageDetail extends EopMessage {
  body_html: string;
  cc_addresses: string[];
  web_link: string;
  attachments: EopAttachment[];
}

export interface EopDraft {
  id: string;
  in_reply_to: string | null;
  conversation_id: string;
  project: string | null;
  subject: string;
  body_html: string;
  to_addresses: string[];
  cc_addresses: string[];
  status: EopDraftStatus;
  created_by: string | null;
  created_by_name: string;
  sent_by: string | null;
  sent_by_name: string;
  sent_at: string | null;
  send_error: string;
  touched_in_outlook: boolean;
  /** Załączniki dołożone z systemu: nazwa + rozmiar (pliki żyją w Exchange). */
  attachments_meta: { name: string; size: number }[];
  created_at: string;
}

export interface EopMailboxStatus {
  enabled: boolean;
  has_credentials: boolean;
  mailbox: string;
  last_poll_at: string | null;
  inbox_watermark: string | null;
  last_error: string;
  messages_total: number;
  messages_general: number;
  messages_unread: number;
  messages_action: number;
  messages_unassigned: number;
  /** Udział nierozpoznanych — miara jakości tagowania, nie ozdoba. */
  unassigned_ratio: number;
  drafts_waiting: number;
  tagging: { layers: EopTaggingLayer[]; assigned_by_hand: number };
}

export interface EopAssignableProject {
  id: string;
  name: string;
  short_name: string;
  client_name: string;
  is_mine: boolean;
}

const KEY = ["eop-mail"];

export interface EopMessageFilters {
  project?: string;
  unassigned?: boolean;
  mine?: boolean;
  direction?: EopDirection | "";
  q?: string;
  /** Jeden wiersz na rozmowę zamiast na wiadomość. */
  groupByThread?: boolean;
}

function toParams(filters: EopMessageFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.project) params.project = filters.project;
  if (filters.unassigned) params.unassigned = "1";
  if (filters.mine) params.mine = "1";
  if (filters.direction) params.direction = filters.direction;
  if (filters.q?.trim()) params.q = filters.q.trim();
  if (filters.groupByThread) params.group = "thread";
  return params;
}

export function useEopMessages(
  filters: EopMessageFilters = {},
  options: { enabled?: boolean } = {},
) {
  const params = toParams(filters);
  return useQuery({
    queryKey: [...KEY, "messages", params],
    enabled: options.enabled ?? true,
    queryFn: () =>
      apiClient
        .get<{ data: EopMessage[] }>("/eop-mail/messages/", { params })
        .then((r) => r.data.data),
    // Odbiór chodzi w tle co 5 minut. Bez odświeżania koordynator patrzy na
    // listę sprzed kwadransa i nie wie, że operator już odpisał.
    refetchInterval: 60000,
  });
}

export function useEopMessage(id: string | null) {
  return useQuery({
    queryKey: [...KEY, "message", id],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: EopMessageDetail }>(`/eop-mail/messages/${id}/`)
        .then((r) => r.data.data),
  });
}

/** Cała rozmowa, od najstarszej wiadomości. */
export function useEopThread(id: string | null) {
  return useQuery({
    queryKey: [...KEY, "thread", id],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: EopMessageDetail[] }>(`/eop-mail/messages/${id}/thread/`)
        .then((r) => r.data.data),
  });
}

export function useEopStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, "status"],
    // Domyślnie włączone; nawigacja pyta tylko dla uprawnionych, żeby reszcie
    // firmy nie lecieć 403 co minutę.
    enabled: options.enabled ?? true,
    queryFn: () =>
      apiClient
        .get<{ data: EopMailboxStatus }>("/eop-mail/status/")
        .then((r) => r.data.data),
    refetchInterval: 60000,
  });
}

export function useEopAssignableProjects() {
  return useQuery({
    queryKey: [...KEY, "projects"],
    queryFn: () =>
      apiClient
        .get<{ data: EopAssignableProject[] }>("/eop-mail/projects/")
        .then((r) => r.data.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useEopDrafts(status?: EopDraftStatus) {
  return useQuery({
    queryKey: [...KEY, "drafts", status ?? "all"],
    queryFn: () =>
      apiClient
        .get<{ data: EopDraft[] }>("/eop-mail/drafts/", {
          params: status ? { status } : {},
        })
        .then((r) => r.data.data),
    refetchInterval: 60000,
  });
}

function useEopMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Redakcja AI szkicu: forma, nigdy treść. Serwer pilnuje, żeby liczby i numery
 * przetrwały co do znaku — inaczej wraca oryginał z notą. */
export function useRefineDraft() {
  return useMutation({
    mutationFn: (payload: { body_html: string; mode: "standard" | "formal" }) =>
      apiClient
        .post<{ data: { body_html: string; changed: boolean; note: string } }>(
          "/eop-mail/refine/",
          payload,
        )
        .then((r) => r.data.data),
  });
}

/** Człowiek potwierdza: ta wiadomość nie dotyczy jednego przedsięwzięcia. */
export function useMarkGeneral() {
  return useEopMutation((id: string) =>
    apiClient
      .post<{ data: EopMessageDetail }>(`/eop-mail/messages/${id}/mark-general/`)
      .then((r) => r.data.data),
  );
}

/** Człowiek wskazuje przedsięwzięcie — to decyzja, nie potwierdzenie propozycji. */
export function useAssignMessage() {
  return useEopMutation(({ id, project }: { id: string; project: string }) =>
    apiClient
      .post<{ data: EopMessageDetail }>(`/eop-mail/messages/${id}/assign/`, {
        project,
      })
      .then((r) => r.data.data),
  );
}

/** Zakłada kopię roboczą odpowiedzi w wątku. NIE wysyła. */
export function useReplyDraft() {
  return useEopMutation(
    ({
      id,
      body_html,
      reply_all,
    }: {
      id: string;
      body_html: string;
      reply_all: boolean;
    }) =>
      apiClient
        .post<{ data: EopDraft }>(`/eop-mail/messages/${id}/reply-draft/`, {
          body_html,
          reply_all,
        })
        .then((r) => r.data.data),
  );
}

/** Kopia robocza PRZEKAZANIA (oryginał + załączniki). NIE wysyła. */
export function useForwardDraft() {
  return useEopMutation(
    ({
      id,
      to,
      cc,
      body_html,
    }: {
      id: string;
      to: string[];
      cc?: string[];
      body_html: string;
    }) =>
      apiClient
        .post<{ data: EopDraft }>(`/eop-mail/messages/${id}/forward-draft/`, {
          to,
          cc: cc ?? [],
          body_html,
        })
        .then((r) => r.data.data),
  );
}

/** Kopia robocza nowego wątku. NIE wysyła. */
export function useCreateDraft() {
  return useEopMutation(
    (payload: {
      subject: string;
      body_html: string;
      to: string[];
      cc?: string[];
      project?: string | null;
    }) =>
      apiClient
        .post<{ data: EopDraft }>("/eop-mail/drafts/", payload)
        .then((r) => r.data.data),
  );
}

export function useUpdateDraft() {
  return useEopMutation(
    ({
      id,
      ...payload
    }: {
      id: string;
      subject?: string;
      body_html?: string;
      to?: string[];
      cc?: string[];
    }) =>
      apiClient
        .patch<{ data: EopDraft }>(`/eop-mail/drafts/${id}/content/`, payload)
        .then((r) => r.data.data),
  );
}

/**
 * JEDYNE wyjście ze skrzynki. Wołane wyłącznie z kliknięcia „Wyślij" —
 * nie z `onSuccess` zapisu, nie z efektu, nie z timera.
 */
/** Stan czytania wątku. `read` podnosi tylko nieprzeczytane (flaga „wymaga
 * akcji" zostaje); `force` zdejmuje też flagę. Zmiana lustruje się do Exchange. */
export function useSetReadState() {
  return useEopMutation(
    ({
      id,
      state,
      force,
    }: {
      id: string;
      state: "read" | "unread" | "action";
      force?: boolean;
    }) =>
      apiClient
        .post<{ data: EopMessageDetail }>(`/eop-mail/messages/${id}/read-state/`, {
          state,
          force: force ?? false,
        })
        .then((r) => r.data.data),
  );
}

/** Załącznik do kopii roboczej — ląduje w Exchange, widzi go też Outlook. */
export function useAttachToDraft() {
  return useEopMutation(({ id, file }: { id: string; file: File }) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient
      .post<{ data: EopDraft }>(`/eop-mail/drafts/${id}/attachments/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  });
}

export function useSendDraft() {
  return useEopMutation((id: string) =>
    apiClient
      .post<{ data: EopDraft }>(`/eop-mail/drafts/${id}/send/`)
      .then((r) => r.data.data),
  );
}

export function useDiscardDraft() {
  return useEopMutation((id: string) =>
    apiClient
      .post<{ data: EopDraft }>(`/eop-mail/drafts/${id}/discard/`)
      .then((r) => r.data.data),
  );
}

export const TAG_SOURCE_LABELS: Record<Exclude<EopTagSource, "">, string> = {
  thread: "z wątku",
  sender: "z nadawcy",
  number: "z numeru",
  parcel: "z działek",
  rule: "z nazwy w temacie",
  ai: "propozycja AI",
  manual: "wskazane ręcznie",
};
